/* eslint-disable max-lines */
import avatarForIntegration from '@/assets/image/free-to-pay/avatar-shelagh-fraser.svg';
import { NewProTierSvg } from '@/assets/image/free-to-pay/new-pro-tier-svg';
import { TogetherTierSvg } from '@/assets/image/free-to-pay/together-tier-svg';
import startsSvg from '@/assets/image/free-to-pay/trust-pilot-starts.svg';
import { Button } from '@heidi/design-system/button';
import {
  TypographyCaption,
  TypographyCaptionBold,
  TypographyH1,
  TypographyP,
  TypographyP2,
  TypographyP2Bold,
  TypographyPBold,
  typographyVariant,
} from '@heidi/design-system/typography';
import { Check, CircleX, X } from 'lucide-react';
import Image from 'next/image';

import { FreeTierSvg } from '@/assets/image/free-to-pay/free-tier-svg';
import { PaywallProBgJpg } from '@/assets/image/free-to-pay/paywall-pro-bg-jpg';
import { PaywallProBgMobileJpg } from '@/assets/image/free-to-pay/paywall-pro-bg-mobile-jpg';
import { usePaywallStates } from '@/atoms/paywallAtoms';
import { useMutateCheckoutV2 } from '@/hooks/mutation/useMutateCheckoutV2';
import { useMutateUpgradePlan } from '@/hooks/mutation/useMutateUpgradePlan';
import {
  getChargingDate,
  getDisplayedPriceFromPlan,
  getPlanFromPeriod,
  getTierProduct,
  useQueryProducts,
} from '@/hooks/query/useQueryProducts';
import { useQueryUser } from '@/hooks/query/useQueryUser';
import { useQueryUserBillingInfo } from '@/hooks/query/useQueryUserBillingInfo';
import useQueryUserTier from '@/hooks/query/useQueryUserTier';
import { cn } from '@/lib/cn';
import { isAppleCompliantBuild } from '@/utils/appConfiguration';
import { enumToDescription } from '@/utils/enumToDescription';
import { getStripeReturnRedirect, stripeCallbackRedirect } from '@/utils/scribeHTTP';
import { PaywallSource, trackPaywallViewed } from '@heidi/analytics/events';
import { TrustPilotSvg, useDurationText } from '@heidi/commerce';
import { Period } from '@heidi/commerce/contracts';
import {
  getRewardfulReferral,
  paywallCheckoutClickedFromUpgrade,
  useReverseTrial,
} from '@heidi/commerce/data';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@heidi/design-system/accordion';
import { Badge } from '@heidi/design-system/badge';
import { Card } from '@heidi/design-system/card';
import { FramedIcon } from '@heidi/design-system/framed-icon';
import {
  DialogDrawer,
  DialogDrawerClose,
  DialogDrawerContent,
  DialogDrawerHeader,
} from '@heidi/design-system/mobile-responsive-dialog-drawer';
import { Skeleton } from '@heidi/design-system/skeleton';
import { useTranslations } from '@heidi/runtime/i18n';
import { StripeFreeTrialDays, SubscriptionTier } from '@heidi/schema/ml-scribe';
import { useCombinedQueriesTeamGeneralInfo } from '@heidi/workspace/data';
import { useCallback, useState } from 'react';
import { Trans } from 'react-i18next';

interface HeidiNewProModalProps {
  presetSelectedTier?: SubscriptionTier;
}

type AccordionItemType = SubscriptionTier | 'NONE';

const HeidiTogetherModal = ({ presetSelectedTier }: HeidiNewProModalProps) => {
  const { t, locale } = useTranslations();
  const { data: userData } = useQueryUser();
  const { upgradeTogetherModalOpen, toggleUpgradeTogetherModal, toggleUpgradeProModal } =
    usePaywallStates();
  const { data: billingInfo } = useQueryUserBillingInfo();
  const { isInReverseTrial } = useReverseTrial();
  const { userTier } = useQueryUserTier();
  const { couponId } = getRewardfulReferral();

  const freeTrialAvailable =
    userData?.free_trial_history_tiers?.includes(SubscriptionTier.PRO_PLUS) === false;

  const initialPreSelectedTier = presetSelectedTier
    ? presetSelectedTier
    : userTier === SubscriptionTier.FREE
      ? SubscriptionTier.PRO
      : userTier === SubscriptionTier.PRO
        ? SubscriptionTier.PRO_PLUS
        : SubscriptionTier.ENTERPRISE;

  const trialDuration = StripeFreeTrialDays.Value14;

  const [currentSelectedTier, setCurrentSelectedTier] = useState<
    SubscriptionTier | undefined
  >(initialPreSelectedTier);
  const [openAccordion, setOpenAccordion] =
    useState<AccordionItemType>(initialPreSelectedTier);
  const onTierChange = (newTier: SubscriptionTier) => {
    setCurrentSelectedTier(newTier);
    setOpenAccordion(openAccordion === newTier ? 'NONE' : newTier);
  };

  const { mutate: createCheckoutRequest } = useMutateCheckoutV2();
  const { mutate: upgradePlan, isPending: isUpgrading } = useMutateUpgradePlan();
  const { data: products } = useQueryProducts();
  const product = getTierProduct(currentSelectedTier, products);
  const shouldShowTrial = !!couponId;
  const { isNonOwnerTeamMember } = useCombinedQueriesTeamGeneralInfo();

  const durationText = useDurationText();

  const period = billingInfo?.plan_cycle || Period.ONE_YEAR;

  const onYearlyCheckout = useCallback(
    (priceTier: SubscriptionTier) => {
      const freeTrialDays = freeTrialAvailable ? trialDuration : undefined;
      if (
        userTier === SubscriptionTier.PRO &&
        priceTier === SubscriptionTier.PRO_PLUS &&
        !isInReverseTrial
      ) {
        void upgradePlan({
          plan_period: Period.ONE_YEAR,
          tier: SubscriptionTier.PRO_PLUS,
          free_trial_days: freeTrialDays,
        });
        return;
      }
      createCheckoutRequest({
        plan_period: Period.ONE_YEAR,
        fe_endpoint: stripeCallbackRedirect,
        fe_return_endpoint: getStripeReturnRedirect(),
        free_trial_days: freeTrialDays,
        tier: priceTier,
      });
    },
    [
      createCheckoutRequest,
      freeTrialAvailable,
      isInReverseTrial,
      trialDuration,
      upgradePlan,
      userTier,
    ]
  );

  if (isAppleCompliantBuild()) {
    return <HeidiTogetherIOSModal />;
  }
  return (
    <>
      {/* mobile overlay */}
      <div
        id="paywall-overlay"
        className={cn(
          'fixed inset-0 z-50 flex size-full flex-col items-start justify-start gap-y-6 bg-transparent p-4 text-text-primary-invert opacity-100 transition-all duration-500 sm:hidden',
          !upgradeTogetherModalOpen && 'z-[-1] opacity-0'
        )}
      >
        <PaywallProBgMobileJpg
          alt=""
          fill
          className="absolute left-0 top-0 z-[-1] object-cover"
        />
        <div id="trust-pilot-review-mobile" className="flex w-full justify-between">
          <div className="flex flex-col items-center justify-between gap-y-1">
            <TrustPilotSvg alt={'trust-pilot'} width={90} height={90} />
            <Image src={startsSvg} alt={'starts'} width={110} height={110} />
            <TypographyCaption className="text-text-primary-invert newBrand:text-text-primary">
              <Trans i18nKey="upgradeModal.trustpilotInfo" components={{ b: <b /> }} />
            </TypographyCaption>
          </div>

          <CircleX
            className="cursor-pointer text-text-primary-invert newBrand:text-text-primary"
            onClick={() => toggleUpgradeTogetherModal(false)}
            width={24}
            height={24}
          />
        </div>
        <div className="flex flex-col items-start gap-y-4">
          <TypographyH1 className="text-3xl text-text-primary-invert newBrand:text-text-primary sm:max-w-87.5 sm:text-4xl lg:max-w-full lg:text-5xl">
            {t('upgradeModal.patientFeedback')}
          </TypographyH1>
          <div className="flex max-w-[400px] items-center gap-3">
            <Image src={avatarForIntegration} alt={'avatar'} height={32} width={32} />
            <div className="flex flex-col">
              <TypographyP2Bold className="text-text-primary-invert newBrand:text-text-primary">
                {t('upgradeModal.sampleUserTitle')}
              </TypographyP2Bold>
              <TypographyCaption className="text-text-primary-invert newBrand:text-text-primary">
                {t('upgradeModal.sampleUserPosition')}
              </TypographyCaption>
            </div>
          </div>
        </div>
      </div>
      {/* dialog drawer */}
      <DialogDrawer
        modal={false}
        dismissible={false}
        open={upgradeTogetherModalOpen}
        onOpenChange={toggleUpgradeTogetherModal}
      >
        <DialogDrawerContent
          disableDrag
          hideOverlay
          hideCloseIcon
          preventFocusOutsideClose
          className="flex max-h-[90dvh] gap-2 overflow-hidden border-none bg-transparent p-0 sm:w-11/12 sm:max-w-2xl sm:p-4 md:max-w-3xl lg:max-w-4xl"
        >
          <PaywallProBgJpg
            alt=""
            fill
            priority
            className="absolute left-0 top-0 z-[-1] object-cover"
          />
          <section className="hidden w-full flex-col justify-between px-2 py-4 md:flex">
            <div className="flex flex-col items-start justify-between gap-y-8">
              <div className="flex gap-2">
                <Badge color="inverted" size="sm">
                  {t('upgradeModal.freeTryBadge')}
                </Badge>
                <Badge color="inverted" size="sm">
                  {t('upgradeModal.cancelAnytimeBadge')}
                </Badge>
              </div>

              <TypographyH1 className="text-text-primary-invert newBrand:text-text-primary sm:text-4xl sm:leading-tight lg:max-w-full lg:text-5xl lg:leading-tight">
                {t('upgradeModal.patientFeedback')}
              </TypographyH1>

              <div className="flex max-w-['400px'] items-center gap-3">
                <Image src={avatarForIntegration} alt={'avatar'} height={32} width={32} />
                <div className="flex flex-col">
                  <TypographyP2Bold className="text-text-primary-invert newBrand:text-text-primary">
                    {t('upgradeModal.sampleUserTitle')}
                  </TypographyP2Bold>
                  <TypographyCaption className="text-text-primary-invert newBrand:text-text-primary">
                    {t('upgradeModal.sampleUserPosition')}
                  </TypographyCaption>
                </div>
              </div>
            </div>
            <div className="flex flex-col justify-between gap-y-1">
              <TrustPilotSvg alt={'trust-pilot'} width={90} height={90} />
              <Image src={startsSvg} alt={'starts'} width={110} height={110} />
              <TypographyCaption className="text-[8px] leading-none text-text-primary-invert newBrand:text-text-primary">
                <Trans i18nKey="upgradeModal.trustpilotInfo" components={{ b: <b /> }} />
              </TypographyCaption>
            </div>
          </section>
          <Card className="w-full rounded-md p-4 hover:shadow-md newBrand:rounded-xl newBrand:hover:shadow-lg sm:max-h-full sm:p-6">
            <Accordion
              type="single"
              collapsible
              value={openAccordion}
              onValueChange={(value: SubscriptionTier) => setOpenAccordion(value)}
              className="flex h-full flex-col gap-y-4 !overflow-visible overflow-y-auto sm:max-h-fit"
            >
              {/* current Free Card */}
              <Card
                id="free-tier-box"
                className={cn(
                  'flex cursor-pointer items-start justify-between p-3.5 hover:shadow-md newBrand:p-4 newBrand:hover:shadow-lg',
                  currentSelectedTier === SubscriptionTier.FREE &&
                    'border-2 border-background-primary',
                  userTier === SubscriptionTier.FREE ? 'hidden sm:flex' : 'hidden'
                )}
                onClick={() => onTierChange(SubscriptionTier.FREE)}
              >
                <div className="flex w-full items-start gap-x-2">
                  <FreeTierSvg alt={'free-tier'} width={32} height={32} />
                  <div className="flex w-full flex-col gap-y-2">
                    <div className="flex items-start justify-between gap-x-0.5">
                      <div>
                        <div className="flex items-center gap-x-1">
                          <TypographyP>{t('upgradeModal.free')}</TypographyP>
                          <Badge color="neutral" size="xs">
                            {t('upgradeModal.current')}
                          </Badge>
                        </div>
                        <TypographyCaption className="text-text-secondary">
                          {t('upgradeModal.freeForEveryone')}
                        </TypographyCaption>
                      </div>
                      <TypographyCaption className="text-text-secondary">
                        {t('upgradeModal.freeForEveryone')}
                      </TypographyCaption>
                    </div>

                    <TypographyP2 className="flex flex-col text-end">
                      {t('upgradeModal.freeForever')}
                    </TypographyP2>
                  </div>

                  <AccordionItem
                    value={SubscriptionTier.FREE}
                    className="border-none"
                    autoFocus={false}
                  >
                    <AccordionTrigger
                      autoFocus={false}
                      onClick={() => onTierChange(SubscriptionTier.FREE)}
                      className={cn(
                        'justify-start py-0 pb-1 text-text-tertiary hover:no-underline newBrand:text-text-link newBrand:hover:text-text-link-hover',
                        typographyVariant({ variant: 'captionBold' })
                      )}
                    >
                      {openAccordion === SubscriptionTier.FREE
                        ? t('upgradeModal.hideInclusions')
                        : t('upgradeModal.showInclusions')}{' '}
                      {t('upgradeModal.inclusions')}
                    </AccordionTrigger>
                    <AccordionContent className="flex flex-col gap-y-2 pb-0 text-text-secondary">
                      <p className="flex items-center gap-x-1">
                        <Check className="size-4" strokeWidth={3} />{' '}
                        {t('upgradeModal.unlimitedTemplates')}
                      </p>
                      <p className="flex items-center gap-x-1">
                        <Check className="size-4" strokeWidth={3} />{' '}
                        {t('upgradeModal.unlimitedDictation')}
                      </p>
                      <p className="flex items-center gap-x-1">
                        <Check className="size-4" strokeWidth={3} />{' '}
                        {t('upgradeModal.proActionsMonthly')}
                      </p>
                      <p className="flex items-center gap-x-1">
                        <Check className="size-4" strokeWidth={3} />{' '}
                        {t('upgradeModal.basicProcessing')}
                      </p>
                      <p className="flex items-center gap-x-1">
                        <Check className="size-4" strokeWidth={3} />
                        {t('upgradeModal.basicCustomerSupport')}
                      </p>
                      <p className="flex items-center gap-x-1 opacity-50">
                        <X className="size-4" strokeWidth={3} />{' '}
                        {t('upgradeModal.noCustomTemplates')}
                      </p>
                      <p className="flex items-center gap-x-1 opacity-50">
                        <X className="size-4" strokeWidth={3} />{' '}
                        {t('upgradeModal.noIntegrations')}
                      </p>
                      <p className="flex items-center gap-x-1 opacity-50">
                        <X className="size-4" strokeWidth={3} />{' '}
                        {t('upgradeModal.noTeamSharing')}
                      </p>
                    </AccordionContent>
                  </AccordionItem>
                </div>
              </Card>

              {/* current Pro Card */}
              <Card
                id="pro-tier-box"
                className={cn(
                  'flex cursor-pointer items-start justify-between bg-surface p-3.5 hover:shadow-md newBrand:p-4 newBrand:hover:shadow-lg',
                  currentSelectedTier === SubscriptionTier.PRO &&
                    'border-2 border-tier-pro'
                )}
                onClick={() => onTierChange(SubscriptionTier.PRO)}
              >
                <div className="flex w-full items-start gap-x-2">
                  <NewProTierSvg alt={'pro-tier-flash'} width={32} height={32} />
                  <div className="flex w-full flex-col gap-y-2">
                    <div className="flex items-start justify-between gap-x-0.5">
                      <div>
                        <div className="flex items-center gap-x-1">
                          <TypographyP>{t('tier.pro')}</TypographyP>
                          {userTier === SubscriptionTier.PRO && (
                            <Badge color="neutral" size="xs">
                              {t('upgradeModal.current')}
                            </Badge>
                          )}
                        </div>

                        <TypographyCaption className="text-text-secondary">
                          {t('upgradeModal.forIndividuals')}
                        </TypographyCaption>
                      </div>

                      <TypographyP2 className="flex flex-col text-end">
                        <PriceDisplay
                          pricePeriod={period}
                          priceTier={SubscriptionTier.PRO}
                        />
                        {period === Period.ONE_YEAR && (
                          <span className="text-xs text-text-secondary">
                            {t('upgradeModal.billedAnnually')}
                          </span>
                        )}
                      </TypographyP2>
                    </div>

                    <AccordionItem
                      autoFocus={false}
                      value={SubscriptionTier.PRO}
                      className="border-none"
                    >
                      <AccordionTrigger
                        autoFocus={false}
                        className={cn(
                          'justify-start py-0 pb-1 text-text-tertiary hover:no-underline newBrand:text-text-link newBrand:hover:text-text-link-hover',
                          typographyVariant({ variant: 'captionBold' })
                        )}
                      >
                        {t('upgradeModal.everythingInFree')}
                      </AccordionTrigger>
                      <AccordionContent className="flex flex-col gap-y-2 pb-0 text-text-secondary">
                        <p className="flex items-center gap-x-1">
                          <Check className="size-4" strokeWidth={3} />{' '}
                          {t('upgradeModal.unlimitedCustomTemplates')}
                        </p>
                        <p className="flex items-center gap-x-1">
                          <Check className="size-4" strokeWidth={3} />{' '}
                          {t('upgradeModal.unlimitedDictation')}
                        </p>
                        <p className="flex items-center gap-x-1">
                          <Check className="size-4" strokeWidth={3} />{' '}
                          {t('upgradeModal.priorityProcessing')}
                        </p>
                        <p className="flex items-center gap-x-1">
                          <Check className="size-4" strokeWidth={3} />{' '}
                          {t('upgradeModal.priorityCustomerSupport')}
                        </p>
                        <p className="flex items-center gap-x-1">
                          <Check className="size-4" strokeWidth={3} />{' '}
                          {t('upgradeModal.teamBilling')}
                        </p>
                        <p className="flex items-center gap-x-1 opacity-50">
                          <X className="size-4" strokeWidth={3} />{' '}
                          {t('upgradeModal.teamSharing')}
                        </p>
                        <p className="flex items-center gap-x-1 opacity-50">
                          <X className="size-4" strokeWidth={3} />{' '}
                          {t('upgradeModal.organisationManagement')}
                        </p>
                        <p className="flex items-center gap-x-1 opacity-50">
                          <X className="size-4" strokeWidth={3} />{' '}
                          {t('upgradeModal.integrations')}
                        </p>
                      </AccordionContent>
                    </AccordionItem>
                  </div>
                </div>
              </Card>

              {/* together Card */}
              <Card
                id="pro-plus-tier-box"
                className={cn(
                  'flex items-start justify-between bg-surface p-3.5 newBrand:p-4',
                  'cursor-pointer hover:shadow-md newBrand:hover:shadow-lg',
                  currentSelectedTier === SubscriptionTier.PRO_PLUS &&
                    'border-2 border-tier-practice'
                )}
                onClick={() => onTierChange(SubscriptionTier.PRO_PLUS)}
              >
                <div className="flex flex-1 items-start justify-between gap-x-2 rounded-md bg-surface">
                  <TogetherTierSvg alt={'together'} width={32} height={32} />
                  <div className="flex w-full flex-1 flex-col gap-y-2">
                    <div className="flex items-start justify-between gap-x-0.5">
                      <div>
                        <TypographyP>{t('tier.practice')}</TypographyP>
                        <TypographyCaption className="text-text-secondary">
                          {t('upgradeModal.forColleagues')}
                        </TypographyCaption>
                      </div>

                      <TypographyP2 className="flex flex-col overflow-visible whitespace-nowrap text-end">
                        <PriceDisplay
                          pricePeriod={Period.ONE_YEAR}
                          priceTier={SubscriptionTier.PRO_PLUS}
                        />
                        <span className="text-xs text-text-secondary">
                          {t('upgradeModal.billedAnnually')}
                        </span>
                      </TypographyP2>
                    </div>
                    <AccordionItem
                      autoFocus={false}
                      value={SubscriptionTier.PRO_PLUS}
                      className="border-none"
                    >
                      <AccordionTrigger
                        autoFocus={false}
                        className={cn(
                          'justify-start py-0 pb-1 text-text-tertiary hover:no-underline newBrand:text-text-link newBrand:hover:text-text-link-hover',
                          typographyVariant({ variant: 'captionBold' })
                        )}
                      >
                        {t('upgradeModal.everythingInPro')}
                      </AccordionTrigger>
                      <AccordionContent className="flex flex-col gap-y-1 pb-0 text-text-secondary">
                        <p className="flex items-center gap-x-1">
                          <Check className="size-4" strokeWidth={3} />{' '}
                          {t('upgradeModal.integrateWithSoftware')}
                        </p>
                        <p className="flex items-center gap-x-1">
                          <Check className="size-4" strokeWidth={3} />{' '}
                          {t('upgradeModal.collaborateWithTeam')}
                        </p>
                        <p className="flex items-center gap-x-1">
                          <Check className="size-4" strokeWidth={3} />{' '}
                          {t('upgradeModal.supportCreatingTemplates')}
                        </p>
                        <p className="flex items-center gap-x-1">
                          <Check className="size-4" strokeWidth={3} />{' '}
                          {t('upgradeModal.prioritySupport')}
                        </p>
                        <p className="flex items-center gap-x-1">
                          <Check className="size-4" strokeWidth={3} />{' '}
                          {t('upgradeModal.codingAutomation')}
                        </p>
                      </AccordionContent>
                    </AccordionItem>
                  </div>
                </div>
              </Card>

              {/* hide for now */}
              {/* enterprise Card */}
              {/* {!hideTiers?.includes(SubscriptionTier.ENTERPRISE) && (
            <Card
              id="enterprise-tier-box"
              className={cn(
                'cursor-pointer p-0.5 hover:shadow-md newBrand:hover:shadow-lg',
                currentSelectedTier === SubscriptionTier.ENTERPRISE &&
                  'bg-gradient-to-r  from-[#A885FF] to-[#8858FF]'
              )}
              onClick={() => onTierChange(SubscriptionTier.ENTERPRISE)}
            >
              <div className="flex items-start justify-between rounded-md bg-surface p-3">
                <div className="flex w-full items-start gap-x-2">
                  <Image
                    src={HeidiProEnterprise}
                    alt="user-grey-profile"
                    width={32}
                    height={32}
                  />
                  <div className="flex w-full flex-col gap-y-2">
                    <div className="flex items-start justify-between gap-x-0.5">
                      <div>
                        <div className="flex items-center gap-x-1">
                          <TypographyP>Enterprise</TypographyP>
                        </div>
                        <TypographyCaption className="text-text-secondary">
                          For large systems, clinics, and hospitals.
                        </TypographyCaption>
                      </div>

                      <TypographyP2 className="text-end">Contact sales</TypographyP2>
                    </div>

                    <AccordionItem
                      value={SubscriptionTier.ENTERPRISE}
                      className="border-none"
                    >
                      <AccordionTrigger className="justify-start py-0 pb-1 text-xs font-normal text-text-tertiary hover:no-underline">
                        Everything in Premium, and ...
                      </AccordionTrigger>
                      <AccordionContent className="flex flex-col gap-y-2 pb-0 pt-2 text-text-secondary">
                        <p className="flex items-center gap-x-1">
                          <Check className="h-4 w-4" strokeWidth={3} /> Deep integrations
                        </p>
                        <p className="flex items-center gap-x-1">
                          <Check className="h-4 w-4" strokeWidth={3} /> Custom terms of
                          service
                        </p>
                        <p className="flex items-center gap-x-1">
                          <Check className="h-4 w-4" strokeWidth={3} />{' '}
                          Organisation-specific silo & hosting
                        </p>
                        <p className="flex items-center gap-x-1">
                          <Check className="h-4 w-4" strokeWidth={3} /> Uptime SLA
                        </p>
                        <p className="flex items-center gap-x-1">
                          <Check className="h-4 w-4" strokeWidth={3} /> Priority customer
                          support with 1-hour response
                        </p>
                        <p className="flex items-center gap-x-1">
                          <Check className="h-4 w-4" strokeWidth={3} /> Dedicated
                          engineering resources
                        </p>
                      </AccordionContent>
                    </AccordionItem>
                  </div>
                </div>
              </div>
            </Card>
          )} */}

              {/* desktop footer */}
              <div className="hidden h-full items-end justify-end gap-x-2 sm:flex">
                {userTier !== currentSelectedTier ? (
                  <div className="flex flex-col items-end gap-y-2">
                    <div className="flex gap-x-2">
                      <DialogDrawerClose asChild>
                        <Button disableAutomaticSvgSizing size="md" variant="ghost">
                          {t('upgradeModal.maybeLater')}
                        </Button>
                      </DialogDrawerClose>
                      {currentSelectedTier === SubscriptionTier.PRO ? (
                        <DialogDrawerClose asChild>
                          <Button
                            disableAutomaticSvgSizing
                            size="md"
                            variant="pro"
                            onClick={() => {
                              toggleUpgradeProModal(true);
                              trackPaywallViewed({
                                source: PaywallSource.UpgradeModalSwitch,
                                target_plan: SubscriptionTier.PRO,
                              });
                            }}
                          >
                            {t('upgradeModal.upgradeToPro')}
                          </Button>
                        </DialogDrawerClose>
                      ) : (
                        <Button
                          disableAutomaticSvgSizing
                          size="md"
                          disabled={isUpgrading || isNonOwnerTeamMember}
                          onClick={() => {
                            if (!currentSelectedTier || isNonOwnerTeamMember) {
                              return;
                            }
                            onYearlyCheckout(currentSelectedTier);
                          }}
                          variant="pro_plus"
                        >
                          {isUpgrading
                            ? t('upgradeModal.upgrading')
                            : freeTrialAvailable
                              ? t('upgradeModal.startTrialMessage', {
                                  days: trialDuration,
                                })
                              : t('upgradeModal.joinHeidiPractice')}
                        </Button>
                      )}
                    </div>
                    {isNonOwnerTeamMember && (
                      <TypographyCaptionBold className="text-end text-validation-warning">
                        {t('upgradeModal.contactTeamOwner')}
                      </TypographyCaptionBold>
                    )}
                  </div>
                ) : (
                  <DialogDrawerClose asChild>
                    <Button
                      disableAutomaticSvgSizing
                      size="md"
                      variant="secondary"
                      onClick={() => toggleUpgradeTogetherModal(false)}
                    >
                      <Trans
                        i18nKey="upgradeModal.stayOnPlanWithSpan"
                        defaults="Stay on <span>{{plan}}</span>"
                        values={{
                          plan: enumToDescription[
                            currentSelectedTier || SubscriptionTier.FREE
                          ],
                        }}
                        components={{
                          span: <span />,
                        }}
                      />
                    </Button>
                  </DialogDrawerClose>
                )}
              </div>

              {/* mobile footer */}
              <div className="flex flex-col items-center gap-y-2 sm:hidden">
                {userTier !== currentSelectedTier ? (
                  <>
                    <Button
                      disableAutomaticSvgSizing
                      size="md"
                      className="w-full"
                      variant={
                        currentSelectedTier === SubscriptionTier.PRO_PLUS
                          ? 'pro_plus'
                          : 'pro'
                      }
                      disabled={isUpgrading}
                      onClick={() => {
                        if (!currentSelectedTier) {
                          return;
                        }
                        paywallCheckoutClickedFromUpgrade();
                        onYearlyCheckout(currentSelectedTier);
                      }}
                    >
                      {isUpgrading
                        ? t('upgradeModal.upgrading')
                        : freeTrialAvailable
                          ? t('upgradeModal.startTrialMessage', { days: trialDuration })
                          : t('upgradeModal.joinHeidiPractice')}
                    </Button>
                    <TypographyCaption className="text-text-secondary">
                      {/* You will not be charged until your free trial ends */}
                      {t('upgradeModal.billedInfo', {
                        price:
                          period === Period.ONE_YEAR
                            ? getDisplayedPriceFromPlan(
                                getPlanFromPeriod(period, product),
                                'year',
                                false,
                                locale,
                                durationText
                              )
                            : getDisplayedPriceFromPlan(
                                getPlanFromPeriod(period, product),
                                'month',
                                true,
                                locale,
                                durationText
                              ),
                        date:
                          billingInfo?.next_billing_date ||
                          getChargingDate({
                            trialLength: trialDuration,
                            shouldShowTrial: shouldShowTrial,
                          }),
                      })}
                    </TypographyCaption>
                    <DialogDrawerClose asChild>
                      <Button
                        disableAutomaticSvgSizing
                        size="md"
                        variant="ghost"
                        className="text-text-secondary"
                      >
                        {t('upgradeModal.maybeLater')}
                      </Button>
                    </DialogDrawerClose>
                  </>
                ) : (
                  <DialogDrawerClose asChild>
                    <Button
                      disableAutomaticSvgSizing
                      size="md"
                      variant="ghost"
                      className="text-text-secondary"
                    >
                      <Trans
                        i18nKey="upgradeModal.stayOnPlanWithSpan"
                        defaults="Stay on <span>{{plan}}</span>"
                        values={{
                          plan: enumToDescription[
                            currentSelectedTier || SubscriptionTier.FREE
                          ],
                        }}
                        components={{
                          span: <span />,
                        }}
                      />
                    </Button>
                  </DialogDrawerClose>
                )}
              </div>
            </Accordion>
          </Card>
        </DialogDrawerContent>
      </DialogDrawer>
    </>
  );
};

const PriceDisplay = ({
  pricePeriod,
  priceTier,
}: {
  pricePeriod: Period;
  priceTier: SubscriptionTier;
}) => {
  const { locale } = useTranslations();
  const { data: products, isPending } = useQueryProducts();
  const product = getTierProduct(priceTier, products);
  const durationText = useDurationText();

  const price = getDisplayedPriceFromPlan(
    getPlanFromPeriod(pricePeriod, product),
    'month',
    true,
    locale,
    durationText
  );

  if (isPending) {
    return <Skeleton />;
  }
  return <>{price}</>;
};

const HeidiTogetherIOSModal = () => {
  const { t } = useTranslations();
  const { upgradeTogetherModalOpen, toggleUpgradeTogetherModal } = usePaywallStates();
  return (
    <DialogDrawer
      open={upgradeTogetherModalOpen}
      onOpenChange={toggleUpgradeTogetherModal}
    >
      <DialogDrawerContent className="mb-5 flex flex-col px-6 sm:mb-0 sm:h-fit sm:w-[500px]">
        <DialogDrawerHeader className="flex flex-col items-start gap-2">
          <FramedIcon state="pro" className="mb-2" />
          <TypographyPBold>{t('upgradeModal.actionRequiresPractice')}</TypographyPBold>
          <TypographyCaptionBold className="text-start">
            {t('upgradeModal.unlockFeatures')}
          </TypographyCaptionBold>

          <div>
            <div className="flex flex-row gap-2">
              <Check className="text-tier-pro-text" />
              <TypographyP2>{t('upgradeModal.unlimitedCustomTemplatesIOS')}</TypographyP2>
            </div>
            <div className="flex flex-row gap-2">
              <Check className="text-tier-pro-text" />
              <TypographyP2>{t('upgradeModal.unlimitedAskHeidiPrompts')}</TypographyP2>
            </div>
            <div className="flex flex-row gap-2">
              <Check className="text-tier-pro-text" />
              <TypographyP2>{t('upgradeModal.unlimitedDocumentTemplates')}</TypographyP2>
            </div>
            <div className="flex flex-row gap-2">
              <Check className="text-tier-pro-text" />
              <TypographyP2>{t('upgradeModal.priorityHumanSupport')}</TypographyP2>
            </div>
          </div>

          <TypographyP2 className="text-text-secondary">
            {t('upgradeModal.cannotUpgradeInApp')}
          </TypographyP2>
        </DialogDrawerHeader>

        <Button
          disableAutomaticSvgSizing
          size="md"
          className="w-full"
          onClick={() => toggleUpgradeTogetherModal(false)}
        >
          {t('common.continueAsFreeUser')}
        </Button>
      </DialogDrawerContent>
    </DialogDrawer>
  );
};

export default HeidiTogetherModal;
